import { FC, useEffect, useState, useMemo, useContext } from 'react';
import { VendorPageContext } from '../../../context';
import { Box, Button, useMediaQuery } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { Table, Pagination, Loader } from '../../../components';
import { getDocuments, downloadPandaDoc } from '../../../fetch';
import { useSnackbar } from 'notistack';
import { formatDate, downloadFile } from '../../../helpers';
import { IDocument } from '../../../models';
import { MobileContract } from './MobileContract';
import { VendorPage } from '../VendorPage';

interface IContracts {}

export const Contracts: FC<IContracts> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { loadingVendors, vendorSageId } = useContext(VendorPageContext);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingContracts, setLoadingContracts] = useState<boolean>(true);
  const [contracts, setContracts] = useState<IDocument[]>([]);
  const [isDownLoading, setDownloading] = useState<boolean>(false);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchContracts = async () => {
    try {
      setLoadingContracts(true);
      const res = await getDocuments({
        page: page + 1,
        perPage: perPage,
        statuses: ['Completed'],
        partnerSageId: vendorSageId
      });
      setRecordCount(res.totalRecordCount);
      setContracts(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading contracts, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingContracts(false);
    }
  };

  const handleDownload = async id => {
    setDownloading(true);
    try {
      const res = await downloadPandaDoc(id);
      const fileHeader = res.headers['content-disposition'];
      // take this ""attachment; filename="Sample Sales Proposal - nhsH9qcdXqejmwipyJhgTP.pdf"; filename*=UTF-8''Sample%20Sales%20Proposal%20-%20nhsH9qcdXqejmwipyJhgTP.pdf""
      // and pull out the correct file name "Sample Sales Proposal - nhsH9qcdXqejmwipyJhgTP.pdf"
      const fileName = fileHeader.split(';')[1].split('filename=')[1].replaceAll('"', '');
      downloadFile(res.data, fileName);
    } catch (error) {
      enqueueSnackbar(`Error, unable to download file, please try again.`, {
        variant: 'error'
      });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSageId, page, perPage]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Date Created',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          return <>{formatDate(original.createdDate)}</>;
        }
      },
      {
        Header: 'Name',
        accessor: 'documentName',
        sort: false
      },

      {
        Header: 'Date Updated',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          return <>{formatDate(original.updatedDate)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          return (
            <Box display="flex" textAlign="right">
              <Button
                id="edit"
                color="primary"
                startIcon={<Visibility />}
                onClick={() => handleDownload(original.documentId)}
              >
                View
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts]);

  return (
    <VendorPage>
      {isDownLoading && <Loader type="fullscreen" position="centered" />}
      <Table
        columns={columns}
        data={contracts}
        isLoading={isLoadingContracts || loadingVendors}
        hidePagination
        mobileProps={{
          handleView: (val: IDocument) => {
            handleDownload(val.documentId);
          }
        }}
        ResponsiveComponent={isMobile ? MobileContract : undefined}
      />
      {!isLoadingContracts && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </VendorPage>
  );
};
